import React, { FC, useEffect, useState } from 'react';
import { checkIfValidUUID, PageTitle } from '../../../layout/core';
import { breadcrumbs } from '../NewApplication';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import {
  getAdminQuickApplicationValidationSchema,
  getPartnerQuickApplicationValidationSchema,
  getQuickApplicationInitialValues,
  QUICK_APPLICATION_FORM_FIELDS,
  QUICK_APPLICATION_FORM_FIELDS_NAMES,
  QuickApplicationTypes
} from '../../../constants/new-application/quick-application/quick-application-form';
import { AccountInformation } from './AccountInformation';
import { useParams } from 'react-router-dom';
import { checkRoles, CLAIMS } from '../../../utils/roles-helper';
import { fetchIntegrationPartnersList, fetchWhiteListedPartnersList } from '../../../store/merchnts/actions/partners';
import { useMerchants } from '../../../store/merchnts/merchants-context';
import { IAuthState } from '../../../redux/AuthRedux';
import { shallowEqual, useSelector } from 'react-redux';
import { RootState } from '../../../../setup';
import { CompanyInformation } from './CompanyInformation';
import SpecialTermsCard from '../SpecialTermsCard';
import { IFeeData } from '../../merchants/FeeTableRow';
import { QuickApplicationFooter } from './QuickApplicationFooter';
import NewApplicationHeader from '../NewApplicationHeader';
import { fetchAccountManagerList } from '../../../store/identyti/actions/application';
import { useIdentity } from '../../../store/identyti/identity-context';
import {
  fetchQuickApplication,
  setQuickApplicationDataToDefault
} from '../../../store/merchnts/actions/quick-application';
import { Spinner } from '../../Spinner';
import { QuickApplicationFees } from './QuickApplicationFees';
import { FEE_INPUTS } from '../../../constants/new-application/quick-application/fee-inputs';
import { parseJwt } from '../../../views/login/Login';
import { fetchAgencyList } from '../../../store/merchnts/actions/agents';

export const QuickApplication: FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const applicationId = checkIfValidUUID(id) ? id : undefined;
  const authUser: IAuthState = useSelector<RootState>(({ auth }) => auth, shallowEqual);
  const { dispatch: IdentityDispatch } = useIdentity();
  const { merchantState, dispatch } = useMerchants();
  const {
    standardFees, merchantFee,
    isQuickApplicationDataLoading,
    quickApplicationData
  } = merchantState;

  const [activeFee, setActiveFee] = useState<IFeeData>(standardFees);
  const [nonStandardFee, setNonStandardFee] = useState<IFeeData>(merchantFee);

  const parsedTokenData = parseJwt(authUser.idToken);
  let partnerId = '';
  let integrationPartnerId = '';
  // TODO check partners permissions
  if (checkRoles(authUser, CLAIMS.PARTNER_ADMIN)) {
    partnerId = parsedTokenData?.['custom:pid'];
  } else if (checkRoles(authUser, CLAIMS.INTEGRATION_PARTNER_ADMIN)) {
    integrationPartnerId = parsedTokenData?.['custom:pid'];
  }

  const getValidationSchema = (): any => {
    if (checkRoles(authUser, CLAIMS.SUPER_ADMIN)) {
      return getAdminQuickApplicationValidationSchema(t);
    }
    if (checkRoles(authUser, CLAIMS.PARTNER_ADMIN)) {
      return getPartnerQuickApplicationValidationSchema(t);
    }
  };

  const formik = useFormik<QuickApplicationTypes>({
    initialValues: getQuickApplicationInitialValues({ partnerId, integrationPartnerId }),
    validationSchema: getValidationSchema(),
    onSubmit: () => void 0,
  });

  useEffect(() => {
    if (applicationId) {
      void fetchQuickApplication({ dispatch, applicationId, formik });
    }
  }, []);

  useEffect(() => {
    void fetchAccountManagerList(IdentityDispatch);
  }, []);

  useEffect(() => {
    if (checkRoles(authUser, CLAIMS.SUPER_ADMIN)) {
      void fetchWhiteListedPartnersList(dispatch);
    }
    if (checkRoles(authUser, CLAIMS.SUPER_ADMIN_AND_ONLY_PARTNER_ADMIN)) {
      void fetchIntegrationPartnersList(dispatch);
    }
  }, []);

  useEffect(() => {
    if (checkRoles(authUser, CLAIMS.SUPER_ADMIN)) {
      void fetchAgencyList(dispatch);
    }
  }, []);

  // useEffect(() => {
  //   void fetchFees(dispatch, activeFee, nonStandardFee);
  // }, []);

  // useEffect(() => {
  //   console.log('quickApplicationData?.fees', quickApplicationData?.fees);
  //   if (quickApplicationData?.fees) {
  //     const newNSF = {
  //       ...nonStandardFee
  //     };
  //
  //     !quickApplicationData?.standardFeeSelected && quickApplicationData?.fees.forEach(el => {
  //       switch (el.name) {
  //         case 'Percentage':
  //           newNSF.percentage = el.amount / 100;
  //           newNSF.percentageId = el.id;
  //           newNSF.minFeeAmount = el?.minimumFeeAmount ? el?.minimumFeeAmount / 100 : '';
  //           break;
  //         case 'Fee Amount':
  //           newNSF.feeAmount = el.amount / 100;
  //           newNSF.feeAmountId = el.id;
  //           break;
  //         case 'Chargeback Fee':
  //           newNSF.chargeback = el.amount / 100;
  //           newNSF.chargebackId = el.id;
  //           break;
  //         case 'Refund Fee':
  //           newNSF.refundFee = el.amount / 100;
  //           newNSF.refundFeeId = el.id;
  //           break;
  //         case 'Risk Fee':
  //           newNSF.riskFee = el.amount / 100;
  //           newNSF.riskFeeId = el.id;
  //           break;
  //       }
  //     });
  //     setNonStandardFee(newNSF);
  //   } else {
  //     console.log('here');
  //     setNonStandardFee(merchantFee);
  //   }
  // }, [quickApplicationData]);

  useEffect(() => {
    return () => {
      setQuickApplicationDataToDefault(dispatch);
    };
  }, []);

  if (isQuickApplicationDataLoading) {
    return (
      <div className="card h-100">
        <div className="card-body">
          <Spinner pageLoader/>
        </div>
      </div>
    );
  }

  const QUICK_APP_FEE_INPUTS = FEE_INPUTS(t);

  return (
    <>
      <PageTitle breadcrumbs={breadcrumbs(t)}>
        {t('NEW_APPLICATION.QUICK_APPLICATION')}
      </PageTitle>
      <NewApplicationHeader />
      <form onSubmit={formik.handleSubmit}>
        <CompanyInformation formik={formik} />
        <AccountInformation formik={formik} />

        {/*<FeesCard*/}
        {/*  formEditStatus={formEditStatus}*/}
        {/*  activeFee={activeFee}*/}
        {/*  applicationId={applicationId}*/}
        {/*  setActiveFee={setActiveFee}*/}
        {/*  nonStandardFee={nonStandardFee}*/}
        {/*  setNonStandardFee={setNonStandardFee}*/}
        {/*/>*/}
        <QuickApplicationFees
          formEditStatus={true}
          formik={formik}
          inputs={QUICK_APP_FEE_INPUTS}
        />

        {
            !formik.values?.[QUICK_APPLICATION_FORM_FIELDS.CONTRACT_ALREADY_SIGNED] && (
                <SpecialTermsCard
                    formik={formik}
                    formEditStatus={true}
                    fieldName={QUICK_APPLICATION_FORM_FIELDS.TERMS_AND_CONDITIONS}
                    label={t(QUICK_APPLICATION_FORM_FIELDS_NAMES.TERMS_AND_CONDITIONS)}
                />
            )
        }

        {
          checkRoles(authUser, CLAIMS.SUPER_ADMIN_AND_ONLY_PARTNER_ADMIN) && (
            <QuickApplicationFooter
              formik={formik}
              activeFee={activeFee}
              nonStandardFee={nonStandardFee}
            />
          )
        }

      </form>
    </>
  );
};
